import { useQueryParams } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { EmptyState as TableEmptyState } from '@calendly/table';
import { BareButton } from '@calendly/ui/components/bare-button';
import { IconHelpSquareRounded } from '@calendly/ui/components/glyphs/IconHelpSquareRounded';
import { IconMessage } from '@calendly/ui/components/glyphs/IconMessage';
import { IconRotateClockwise } from '@calendly/ui/components/glyphs/IconRotateClockwise';
import { IconX } from '@calendly/ui/components/glyphs/IconX';
import { Link } from '@calendly/ui/components/link';
import { Loader } from '@calendly/ui/components/loader';
import { BodyText, TitleText } from '@calendly/ui/components/typography';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { colorTextSecondary } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { titleSelector } from 'selectors/home_bar';
const emptyWrapperCss = "e1q5xvbp";
const EmptyWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(emptyWrapperCss, props.className)} />;
});
const buttonWrapperCss = "b11oe8mx";
const ButtonWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(buttonWrapperCss, props.className)} />;
});
const titleWrapperCss = "tawbp75";
const TitleWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(titleWrapperCss, props.className)} />;
});
const descriptionCss = "d6tmx9s";
const Description = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(descriptionCss, props.className)} />;
});
const emptyStateButtonCss = "e173xbds";
const EmptyStateButton = forwardRef((props, ref) => <BareButton ref={ref} {...props} className={classNames(emptyStateButtonCss, props.className)} />);
const styledLinkCss = "s8ia8uu";
const StyledLink = forwardRef((props, ref) => <Link ref={ref} {...props} className={classNames(styledLinkCss, props.className)} />);
const nudgedHelpIconCss = "n1t8q83k";
const NudgedHelpIcon = forwardRef((props, ref) => <span ref={ref} {...props} className={classNames(nudgedHelpIconCss, props.className)}>
    <UiIcon icon={IconHelpSquareRounded} size="small" />
  </span>);
const helpCenterLinkCss = "h12jhl8b";
const HelpCenterLink = forwardRef((props, ref) => {
  const href = '/help';
  const target = '_blank';
  const rel = 'noopener noreferrer';
  const underline = false;
  return <StyledLink ref={ref} {...props} href={href} target={target} rel={rel} underline={underline} className={classNames(helpCenterLinkCss, props.className)}>
      <NudgedHelpIcon />
      {I18n.t('publisher.scheduling.shared.empty_state.scope.button.visit_help')}
    </StyledLink>;
});
const supportChatLinkCss = "smc4emu";
const SupportChatLink = forwardRef((props, ref) => {
  const href = 'https://calendly.com/help/support';
  const target = '_blank';
  const rel = 'noopener noreferrer';
  const underline = false;
  return <StyledLink ref={ref} {...props} href={href} target={target} rel={rel} underline={underline} className={classNames(supportChatLinkCss, props.className)}>
      <>
        <UiIcon icon={IconMessage} size="small" />
        {I18n.t('publisher.scheduling.shared.empty_state.share.button.chat_with_support')}
      </>
    </StyledLink>;
});
const EmptyStateWrapper = ({
  title,
  description,
  children
}) => <TableEmptyState>
    <EmptyWrapper>
      {title && <TitleWrapper>
          <TitleText level={2} size="large">
            {title}
          </TitleText>
        </TitleWrapper>}
      {description && <Description>
          <BodyText color={colorTextSecondary} size="medium">{description}</BodyText>
        </Description>}
      {children}
    </EmptyWrapper>
  </TableEmptyState>;

// Breaking up the translation like this allows us to safely add emphasis to the search term
// without the need for unsafe parsing of HTML or Markdown
const SearchDescription = ({
  searchTerm
}) => <>
    {I18n.t('publisher.scheduling.shared.empty_state.search.descriptionBefore')}
    <strong>
      {searchTerm}
    </strong>
    {I18n.t('publisher.scheduling.shared.empty_state.search.descriptionAfter')}
  </>;
export const EmptyState = ({
  isError,
  isLoading,
  refetch
}) => {
  const queryParams = useQueryParams();
  const history = useHistory();
  const scopeName = useSelector(titleSelector);
  const removeFilters = () => {
    const newParams = {
      ...queryParams
    };
    delete newParams.status;
    history.push(newParams);
  };
  if (isLoading) {
    return <EmptyStateWrapper>
        <Loader data-testid="single_use_links-table-loader" />
      </EmptyStateWrapper>;
  }
  if (isError) {
    return <EmptyStateWrapper title={I18n.t('publisher.scheduling.shared.empty_state.error.title')} description={I18n.t('publisher.scheduling.single_use_links.empty_state.error.description')}>
        <ButtonWrapper>
          <EmptyStateButton onClick={refetch}>
            <UiIcon icon={IconRotateClockwise} size="small" spacingRight="4" />
            {I18n.t('publisher.scheduling.shared.empty_state.error.button.retry')}
          </EmptyStateButton>
          <SupportChatLink />
        </ButtonWrapper>
      </EmptyStateWrapper>;
  }
  if (queryParams.filter_term) {
    return <EmptyStateWrapper title={I18n.t('publisher.scheduling.shared.empty_state.search.title')} description={<SearchDescription searchTerm={queryParams.filter_term} />} />;
  }
  if (queryParams.event_type_ids || queryParams.period || queryParams.start_date || queryParams.end_date) {
    return <EmptyStateWrapper title={I18n.t('publisher.scheduling.shared.empty_state.search.title')} description={I18n.t('publisher.scheduling.shared.empty_state.filter.description')}>
        <EmptyStateButton onClick={removeFilters}>
          <UiIcon icon={IconX} size="small" spacingRight="4" spacingBottom="4" />
          {I18n.t('publisher.scheduling.shared.empty_state.filter.button.clear')}
        </EmptyStateButton>
      </EmptyStateWrapper>;
  }
  return <EmptyStateWrapper title={I18n.t('publisher.scheduling.single_use_links.empty_state.scope.title', {
    scopeName
  })} description={I18n.t('publisher.scheduling.single_use_links.empty_state.scope.description')}>
      <ButtonWrapper>
        <HelpCenterLink />
        <SupportChatLink />
      </ButtonWrapper>
    </EmptyStateWrapper>;
};

require("./empty_state.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./empty_state.js");